import { useEffect, createContext, useContext, useState } from 'react';
import { createConsumer } from '@rails/actioncable';
import axios from 'axios';
import { get } from 'lodash';
import { Toaster } from '@components';

const gridCols = 'grid-cols-[2fr_1fr_1.5fr] lg:grid-cols-[3.5fr_80px_140px_100px_0.5fr_250px]';

let refetchTimeout;

export const DealContext = createContext();

export const useDeal = () => {
  const context = useContext(DealContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a DealProvider');
  }

  return context;
};

const storage = {
  get: key => {
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
  },
  write: (key, ids = []) => {
    localStorage.setItem(key, JSON.stringify(ids));
  }
};

const channelConsumer = createConsumer();

const subscribe = callback => {
  return channelConsumer.subscriptions.create(
    { channel: 'UserChannel' },
    {
      received(data) {
        clearTimeout(refetchTimeout);
        refetchTimeout = setTimeout(() => callback(data), 500);
      }
    }
  );
};

export const DealProvider = ({ dealId, children }) => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [deal, setDeal] = useState();
  const [taskItemsGroups, setTaskItemsGroups] = useState([]);
  const [taskGroupById, setTaskGroupById] = useState({});
  const [taskItemById, setTaskItemById] = useState({});
  const [availableStates, setAvailableStates] = useState([]);
  const [authStatuses, setAuthStatuses] = useState({});
  const [dealObjects, setDealObjects] = useState({});
  const [currentUser, setCurrentUser] = useState();

  // GROUPS
  const groupsStorageKey = `deal-groups-${dealId}`;
  const [closedGroupIds, setClosedGroupIds] = useState([]);

  const toggleGroup = groupId => {
    const storedGroupIds = storage.get(groupsStorageKey);
    let newGroupIds;
    if (Array.isArray(storedGroupIds) && storedGroupIds.includes(String(groupId))) {
      newGroupIds = storedGroupIds.filter(id => id !== String(groupId));
    } else {
      const safeArray = Array.isArray(storedGroupIds) ? storedGroupIds : [];
      newGroupIds = [...safeArray, String(groupId)];
    }
    storage.write(groupsStorageKey, newGroupIds);
    setClosedGroupIds(newGroupIds);
  };

  const toggleAllGroups = () => {
    const groupIds = Object.keys(taskGroupById);
    const storedGroupIds = storage.get(groupsStorageKey);

    if (Array.isArray(storedGroupIds) && storedGroupIds.length === groupIds.length) {
      storage.write(groupsStorageKey, []);
      setClosedGroupIds([]);
    } else {
      storage.write(groupsStorageKey, groupIds);
      setClosedGroupIds(groupIds);
    }
  };

  const fetchDealObjects = async () => {
    const { data } = await axios.get(`/shared/deals/${dealId}/objects`);
    setDealObjects(data);
  };

  useEffect(() => {
    fetchDealObjects();
  }, [dealId]);

  const onChannelUpdate = (data = {}) => {
    const taskItemId = get(data, 'task_item_id');
    if (!!taskItemId && !loading) {
      fetchData();
    }
  };

  useEffect(() => {
    const storedClosedGroupIds = storage.get(groupsStorageKey);
    if (Array.isArray(storedClosedGroupIds) && storedClosedGroupIds.length > 0) {
      setClosedGroupIds(storedClosedGroupIds);
    }
  }, [taskGroupById]);

  async function fetchData() {
    if (loading) {
      return;
    }

    setLoading(true);

    const {
      data: { auth_statuses = {}, available_states = [], current_user, deal, groups = [] } = {}
    } = await axios.get(`/guest/deals/${dealId}/task_groups`);

    setCurrentUser(current_user);
    setDeal(deal);
    setTaskItemsGroups(groups);
    setAvailableStates(available_states);
    setAuthStatuses(auth_statuses);

    setTaskItemById(
      groups.reduce((acc, g) => {
        g.task_items.forEach(ti => (acc[ti.id] = ti));
        return acc;
      }, {})
    );

    setTaskGroupById(
      groups.reduce((acc, g) => {
        acc[g.id] = g;
        return acc;
      }, {})
    );

    setLoading(false);
    setInitialLoading(false);
  }

  useEffect(() => {
    fetchData();

    const subscription = subscribe(onChannelUpdate);

    return () => {
      subscription.unsubscribe();
    };
  }, [dealId]);

  const value = {
    gridCols,
    dealId,
    initialLoading,
    loading,
    deal,
    taskItemsGroups,
    taskGroupById,
    taskItemById,
    availableStates,
    authStatuses,
    toggleGroup,
    toggleAllGroups,
    closedGroupIds,
    dealObjects,
    currentUser
  };

  return (
    <DealContext.Provider value={value}>
      {children}
      <Toaster position="top-right" />
    </DealContext.Provider>
  );
};
